import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
    Plus,
    Edit2,
    Trash2,
    Play,
    Pause,
    User,
    AlertCircle,
    CheckCircle,
    Filter,
    Calendar,
    GripVertical
} from 'lucide-react';
import apiService from '../services/api';
import { formatDateWithDefaultTimezone } from '../utils/timezone';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        status: 'paused',
        priority: 1
    });
    const [defaultTimezone, setDefaultTimezone] = useState('UTC');
    useEffect(() => {
        fetchUsers();
        fetchConfig();
    }, []);

    const fetchUsers = async () => {
        try {
            setError('');

            const usersData = await apiService.getUsers();
            // Sort users by priority for proper display
            const sortedUsers = usersData.sort((a, b) => a.priority - b.priority);
            setUsers(sortedUsers);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const fetchConfig = async () => {
        try {
            const configData = await apiService.getConfig();
            const defaultConfig = configData.find(config => config.username === null);
            if (defaultConfig && defaultConfig.timeZone) {
                setDefaultTimezone(defaultConfig.timeZone);
            }
        } catch (err) {
            console.error('Error fetching config:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.username || !formData.password) {
            setError('Username and password are required');
            return;
        }

        try {
            setError('');
            if (editingUser) {
                await apiService.updateUser(editingUser.username, {
                    password: formData.password,
                    status: formData.status,
                    priority: parseInt(formData.priority - 1)
                });
            } else {
                await apiService.createUser({
                    username: formData.username,
                    password: formData.password,
                    status: formData.status,
                    priority: parseInt(formData.priority - 1)
                });
            }

            await fetchUsers();
            closeModal();
        } catch (err) {
            console.error('Error saving user:', err);
            const errorMessage = err.response?.data?.error || 'Failed to save user';

            // Show more user-friendly messages for constraint violations
            if (errorMessage.includes('Username already exists')) {
                setError('This username already exists.');
            } else if (errorMessage.includes('Priority already exists')) {
                setError('This priority is already assigned to another user. Please choose a different priority.');
            } else {
                setError(errorMessage);
            }
        }
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({
            username: user.username,
            password: '', // Don't pre-fill password for security
            status: user.status,
            priority: user.priority + 1
        });
        setShowModal(true);
    };

    const handleDelete = async (username) => {
        if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
            return;
        }

        try {
            setError('');
            await apiService.deleteUser(username);
            await fetchUsers();
        } catch (err) {
            console.error('Error deleting user:', err);
            setError(err.response?.data?.error || 'Failed to delete user');
        }
    };

    const handleStatusToggle = async (username, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'paused' : 'active';

        try {
            setError('');
            await apiService.updateUser(username, { status: newStatus });
            await fetchUsers();
        } catch (err) {
            console.error('Error updating user status:', err);
            const errorMessage = err.response?.data?.error || 'Failed to update user status';

            if (errorMessage.includes('Priority already exists')) {
                setError('Cannot update user: Priority conflict occurred. Please try again.');
            } else {
                setError(errorMessage);
            }
        }
    };

    const openModal = async () => {
        setEditingUser(null);
        setFormData({
            username: '',
            password: '',
            status: 'paused'
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingUser(null);
        setError('');
    };

    const handleDragEnd = async (result) => {
        if (!result.destination) {
            return;
        }

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        if (sourceIndex === destinationIndex) {
            return;
        }

        // Create a new array with reordered users
        const newUsers = Array.from(users);
        const [reorderedUser] = newUsers.splice(sourceIndex, 1);
        newUsers.splice(destinationIndex, 0, reorderedUser);

        // Update local state immediately for smooth UX
        setUsers(newUsers);

        try {
            // Update priorities on the server
            const usernames = newUsers.map(user => user.username);
            await apiService.updatePriorities(usernames);

            // Refresh users to get updated priorities from server
            await fetchUsers();
        } catch (err) {
            console.error('Error updating priorities:', err);
            setError('Failed to update user priorities');

            // Revert local changes on error
            setUsers(users);
        }
    };
    const formatDate = (date) => {
        return formatDateWithDefaultTimezone(date, defaultTimezone);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
                <button
                    onClick={openModal}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Add User
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                </div>
            )}

            {/* Users Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900">Users ({users.length})</h2>
                        <p className="text-sm text-gray-500">Drag rows to reorder priority</p>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Priority
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    User
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Last Lead Found
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Last Lead Claimed
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="users">
                                {(provided, snapshot) => (
                                    <tbody
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className={`bg-white divide-y divide-gray-200 ${snapshot.isDraggingOver ? 'bg-blue-50' : ''
                                            }`}
                                    >
                                        {users.map((user, index) => (
                                            <Draggable key={user.username} draggableId={user.username} index={index}>
                                                {(provided, snapshot) => (
                                                    <tr
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        className={`hover:bg-gray-50 transition-colors ${snapshot.isDragging ? 'bg-blue-100 shadow-lg z-10' : ''
                                                            }`}
                                                        style={{
                                                            ...provided.draggableProps.style,
                                                        }}
                                                    >
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            <div className="flex items-center gap-3">
                                                                <button
                                                                    type="button"
                                                                    {...provided.dragHandleProps}
                                                                    className="cursor-grab active:cursor-grabbing p-2 rounded hover:bg-gray-200 transition-colors flex items-center justify-center border-none bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                    title="Drag to reorder"
                                                                    style={{ touchAction: 'none' }}
                                                                    onMouseDown={(e) => e.stopPropagation()}
                                                                >
                                                                    <GripVertical className="w-4 h-4 text-gray-500" />
                                                                </button>
                                                                <span className="font-medium">{user.priority + 1}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                <User className="w-5 h-5 text-gray-400 mr-3" />
                                                                <div>
                                                                    <div className="text-sm font-medium text-gray-900">{user.username}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.status === 'active'
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : 'bg-gray-100 text-gray-800'
                                                                }`}>
                                                                {user.status === 'active' ? (
                                                                    <CheckCircle className="w-3 h-3 mr-1" />
                                                                ) : (
                                                                    <Pause className="w-3 h-3 mr-1" />
                                                                )}
                                                                {user.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {formatDate(user.lastLeadFoundAt)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {formatDate(user.lastLeadClaimedAt)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                            <div className="flex items-center gap-2 justify-end">
                                                                <button
                                                                    onClick={() => handleStatusToggle(user.username, user.status)}
                                                                    className={`p-2 rounded-lg transition-colors ${user.status === 'active'
                                                                            ? 'text-yellow-600 hover:bg-yellow-50'
                                                                            : 'text-green-600 hover:bg-green-50'
                                                                        }`}
                                                                    title={user.status === 'active' ? 'Pause user' : 'Activate user'}
                                                                >
                                                                    {user.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                                                                </button>
                                                                <button
                                                                    onClick={() => handleEdit(user)}
                                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                    title="Edit user"
                                                                >
                                                                    <Edit2 className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDelete(user.username)}
                                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                    title="Delete user"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </tbody>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </table>

                    {users.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                            No users found. Click "Add User" to create your first user.
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit User Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h2 className="text-xl font-semibold mb-4">
                            {editingUser ? 'Edit User' : 'Add New User'}
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Username
                                </label>
                                <input
                                    type="text"
                                    value={formData.username}
                                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                    disabled={editingUser !== null}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Password {editingUser && '(leave empty to keep current)'}
                                </label>
                                <input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required={!editingUser}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Status
                                </label>
                                <select
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="paused">Paused</option>
                                    <option value="active">Active</option>
                                </select>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="submit"
                                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    {editingUser ? 'Update' : 'Create'}
                                </button>
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
